import { useCallback, useState } from "react";
import { useGlobalStateContext } from "@/context/GlobalContext";
// Removed old status bar import - no longer needed
import { useUnifiedTabConfiguration } from "./useUnifiedTabConfiguration";
import { 
  getFormatedStatus,
  tastingPanelWorkflowStatusMap,
  prePanelAndBusinessReviewWorkflowStatusMap,
  vendorInformationWorkflowStatusMap,
  productInformationWorkflowStatusMap
} from "@/utils/helper";
import { useFormContext } from "@/context/FormContext";
import Constants from "@/static/constants/constants.json";
import { usePathname } from "next/navigation";
import { usePreviousStateContext, PreviousStateItem } from "@/context/PreviousStateContext";

// SSR guard helper
const isServer = typeof window === 'undefined';

// Simplified status step type - no longer depends on status bar data structure
type StatusStep = {
  stepNo: number;
  tabIndex: number;
  stepperBackgroundColor: string;
  statusMap: string[];
};

const notApplicableStatuses = [Constants.ARCHIVED_STATUS.toLocaleLowerCase(), Constants.ON_HOLD_STATUS.toLocaleLowerCase()];
const archivedStatus = Constants.ARCHIVED_STATUS.toLocaleLowerCase();

// Workflow status maps for different tab types - cascading approach (same pattern as helper.ts)
// Using the same approach as tastingPanelStatusMap and prePanelProductStatusMap
const WORKFLOW_STATUS_MAPS = {
  PRODUCT_INFORMATION: productInformationWorkflowStatusMap,
  VENDOR_INFORMATION: vendorInformationWorkflowStatusMap,
  PREPANEL_AND_BUSINESS_REVIEW: prePanelAndBusinessReviewWorkflowStatusMap,
  TASTING_PANEL: tastingPanelWorkflowStatusMap
};

const useTabStateValidator = (selectedVendorId?: string) => {
  // Always call hooks first to maintain consistent order
  const { setActiveInfoFormIndex, setActiveVendorTab, activeVendorTab, activeInfoFormIndex } = useGlobalStateContext();
  const { statusesOfVendorObj, statusesOfProduct } = useFormContext();
  const [selectedTastingPanelTab, setSelectedTastingPanelTab] = useState<number>(0);
  const [selectedMainTab, setSelectedMainTab] = useState<number>(0);
  const pathname = usePathname();
  const { previousStateList, getPreviousStateByVendor , getPreviousStateByApiSource } = usePreviousStateContext();
  // New unified tab configuration - must be called before conditional return
  const unifiedTabConfig = useUnifiedTabConfiguration(selectedVendorId || '');

  // Helper functions needed by useCallback - must be defined before hooks
  const getApiSourcesFromPath = (): PreviousStateItem['apiSource'][] => {
    if (pathname.includes('shopping-list-management')) {
      return ['shoppinglist'];
    } else if (pathname.includes('vendor-assessment')) {
      return ['vendorAssessmentList'];
    } else if (pathname.includes('prepare-for-panel')) {
      return ['prePanelList'];
    } else if (pathname.includes('business-review-panel')) {
      return ['businessReviewPanelList'];
    } else if (pathname.includes('tasting-panel')) {
      return ['tastingPanelList'];
    } else {
      // Default: check all API sources
      return ['shoppinglist', 'vendorAssessmentList', 'prePanelList', 'businessReviewPanelList', 'tastingPanelList'];
    }
  };

  const getVendorWorkflowStatus = (vendorId: string): string | null => {
    const apiSources = getApiSourcesFromPath();
    
    // Check each API source for vendor's previous state
    for (const apiSource of apiSources) {
      const previousStateItems = getPreviousStateByApiSource(apiSource);
      const vendorPreviousState = previousStateItems.find(item => item.vendorId === vendorId);
      
      if (vendorPreviousState && vendorPreviousState.previousState) {
        return vendorPreviousState.previousState.toLowerCase();
      }
    }
    
    return null;
  };

  const getHighestTabIndexFromWorkflowStatus = (vendorId: string): number => {
    const workflowStatus = getVendorWorkflowStatus(vendorId);
    
    if (!workflowStatus) {
      return 0;
    }
    
    // Check workflow status maps in descending order to find highest tab level
    // Map workflow levels to tabIndex values based on statusBarData structure
    if (WORKFLOW_STATUS_MAPS.TASTING_PANEL.includes(workflowStatus)) {
      return 4; // TASTING_PANEL tab
    } else if (WORKFLOW_STATUS_MAPS.PREPANEL_AND_BUSINESS_REVIEW.includes(workflowStatus)) {
      return 3; // BUSINESS_REVIEW tab  
    } else if (WORKFLOW_STATUS_MAPS.VENDOR_INFORMATION.includes(workflowStatus)) {
      return 1; // VENDOR_INFORMATION tab
    } else if (WORKFLOW_STATUS_MAPS.PRODUCT_INFORMATION.includes(workflowStatus)) {
      return 0; // PRODUCT_INFORMATION tab
    }
    
    return 0; // Default to first tab if workflow status not recognized
  };

  // useCallback hook must be called before conditional return
  const toggleVendorActiveTabs = useCallback((vendorStatus: string, vendorIndex: number = -1, vendorId?: string) => {
    // Default minimal behavior: compute highest tab via workflow when archived/onhold, else default to 1
    let highestTabIndex = 1;

    if (vendorId && notApplicableStatuses.includes(vendorStatus?.toLowerCase())) {
      const workflowTabIndex = getHighestTabIndexFromWorkflowStatus(vendorId);
      if (workflowTabIndex > 0) {
        highestTabIndex = workflowTabIndex;
      }
    }

    setActiveInfoFormIndex(highestTabIndex);
    if(vendorIndex !== -1){ 
      setSelectedTastingPanelTab(vendorIndex);
      setActiveVendorTab(vendorIndex);
      setSelectedMainTab(vendorIndex);
    }
    return highestTabIndex;
  }, [setActiveInfoFormIndex, setActiveVendorTab]);

  // Server-side: return stable, no-op API to avoid crashes where hook is imported
  if (isServer) {
    const noop = () => {};
    return {
      toggleVendorActiveTabs: () => 0,
      selectedTastingPanelTab: 0,
      setSelectedTastingPanelTab: noop,
      shouldEnabelTabForVendor: () => false,
      selectedMainTab: 0,
      setSelectedMainTab: noop,
      isTabEnabledUnified: () => false,
      getTabStateUnified: () => ({} as any),
      getEnabledTabsUnified: [] as any,
      // spread to keep shape parity
      ...{
        isTabEnabled: () => false,
        getTabState: () => ({} as any),
        enabledTabs: [] as any
      }
    } as any;
  }


  const shouldEnabelTabForVendor = (statusMap: string[], selectedVendorId: string, tabType?: 'PRODUCT_INFORMATION' | 'VENDOR_INFORMATION' | 'PREPANEL_AND_BUSINESS_REVIEW' | 'TASTING_PANEL') => {
    const vendorStatus = statusesOfVendorObj[selectedVendorId]?.status;
    
    let shouldEnable = false;
    if (vendorStatus) { 
      if (vendorStatus?.toLowerCase() == archivedStatus) {
        // For archived/onhold vendors, check previous state and workflow status
        shouldEnable = checkPreviousStateWorkflowStatus(selectedVendorId, tabType);
      } else {
        let isVendorOnHoldInBusinessReview = vendorStatus?.toLowerCase() == Constants.HOLD_STATUS?.toLowerCase() && tabType == "PREPANEL_AND_BUSINESS_REVIEW";
        shouldEnable = statusMap.includes(vendorStatus?.toLowerCase()) || isVendorOnHoldInBusinessReview;
      }
    } else {
      shouldEnable = statusMap.includes(statusesOfProduct?.workflowStatus?.toLocaleLowerCase());
    }
    
    return shouldEnable;
  };

  const checkPreviousStateWorkflowStatus = (selectedVendorId: string, tabType?: 'PRODUCT_INFORMATION' | 'VENDOR_INFORMATION' | 'PREPANEL_AND_BUSINESS_REVIEW' | 'TASTING_PANEL'): boolean => {
    if (!tabType) return false;
    
    // Get the workflow status map for this tab type (similar to existing statusMap approach)
    const workflowStatusMap = WORKFLOW_STATUS_MAPS[tabType];
    if (!workflowStatusMap) return false;
    
    // Get workflow status from previous state using shared helper
    const workflowStatus = getVendorWorkflowStatus(selectedVendorId);
    if (workflowStatus) {
      // Check if workflow status is in the status map for this tab (similar to statusMap.includes() logic)
      if (workflowStatusMap.includes(workflowStatus)) {
        return true;
      }
    }
    
    // Also check vendor's current workflow status from statusesOfVendorObj
    const vendorWorkflowStatus = statusesOfVendorObj[selectedVendorId]?.workflowStatus;
    if (Array.isArray(vendorWorkflowStatus)) {
      const hasMatchingWorkflow = vendorWorkflowStatus.some(status => 
        workflowStatusMap.includes(status.toLowerCase())
      );
      if (hasMatchingWorkflow) {
        return true;
      }
    }
    
    return false;
  };

  return {
    // Legacy functions (for backward compatibility)
    toggleVendorActiveTabs,
    selectedTastingPanelTab,
    setSelectedTastingPanelTab,
    shouldEnabelTabForVendor,
    selectedMainTab,
    setSelectedMainTab,
    
    // New unified functions
    ...unifiedTabConfig,
    isTabEnabledUnified: unifiedTabConfig.isTabEnabled,
    getTabStateUnified: unifiedTabConfig.getTabState,
    getEnabledTabsUnified: unifiedTabConfig.enabledTabs,
  };
};

export default useTabStateValidator;